import { io, Socket } from "socket.io-client";

/** URL del servidor de tiempo real de esta plataforma, o "" si no tiene. */
const socketUrl = (): string => (import.meta.env.VITE_CHAT_SOCKET_URL ?? "").trim();

/**
 * ¿Esta plataforma tiene tiempo real? Sin `VITE_CHAT_SOCKET_URL` no se abre
 * ningún socket: el chat funciona sólo por REST y lo nuevo se ve al actualizar.
 */
export const isChatSocketConfigured = (): boolean => socketUrl() !== "";

let socket: Socket | null = null;

export interface SocketMessage {
  id: string;
  chat_id: string;
  sender_id: string;
  sender_name: string;
  content: string;
  type: "text" | "file" | "system";
  timestamp: number;
  read_by: string[];
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_type?: string | null;
  attachment_size?: number | null;
}

export interface TypingEvent {
  chat_id: string;
  user_id: string;
  user_name?: string;
}

export interface PresenceEvent {
  user_id: string;
  is_online: boolean;
  last_seen?: number;
}

/**
 * Payload del evento `chat-created` (lo emite el backend vía relay
 * server-side cuando otro participante crea un chat). Si trae `chat`
 * completo, el receptor lo upsertea directo; si solo trae `chat_id`,
 * refresca la lista.
 */
export interface ChatCreatedPayload {
  chat_id: string;
  participant_ids?: string[];
  chat?: unknown;
}

// We track which token+user the live socket was created with so a re-init
// with the same identity is a no-op, but a re-init with different credentials
// (login as another user) replaces the connection cleanly.
let activeAuthKey: string | null = null;

/**
 * Initialize socket connection with authentication. Idempotent for the same
 * (token, userId) pair; replaces the connection if credentials change.
 * Devuelve `null` si la plataforma no tiene servidor de tiempo real.
 */
export const initChatSocket = (token: string, userId: string): Socket | null => {
  const url = socketUrl();
  if (!url) return null;

  const authKey = `${userId}::${token}`;
  if (socket && activeAuthKey === authKey) {
    // Same identity – return the existing instance untouched so we don't
    // accumulate duplicate listeners on every reload.
    return socket;
  }

  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  activeAuthKey = authKey;

  socket = io(url, {
    auth: { token: `Bearer ${token}` },
    query: { userId },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    randomizationFactor: 0.5,
    timeout: 20000,
  });

  socket.on("connect", () => {
    console.log("[ChatSocket] Connected:", socket?.id);
    // Refresh the presence snapshot on every (re)connection so the UI does
    // not miss users that were already online before we joined.
    socket?.emit("get-online-users");
  });

  socket.io.on("reconnect", (attempt) => {
    console.log(`[ChatSocket] Reconnected after ${attempt} attempt(s)`);
    socket?.emit("get-online-users");
  });

  socket.io.on("reconnect_attempt", (attempt) => {
    console.log(`[ChatSocket] Reconnect attempt #${attempt}`);
  });

  socket.on("disconnect", (reason) => {
    console.log("[ChatSocket] Disconnected:", reason);
    // io server-side disconnect (e.g. kicked) won't auto-reconnect by default.
    // Force a reconnect so presence and message relay come back.
    if (reason === "io server disconnect") {
      socket?.connect();
    }
  });

  socket.on("connect_error", (error) => {
    console.error("[ChatSocket] Connection error:", error.message);
  });

  return socket;
};

export const getChatSocket = (): Socket | null => socket;

export const disconnectChatSocket = (): void => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  activeAuthKey = null;
};

export const isSocketConnected = (): boolean => socket?.connected === true;

export const sendTyping = (chat_id: string): void => {
  socket?.emit("typing", { chat_id });
};

export const sendStopTyping = (chat_id: string): void => {
  socket?.emit("stop-typing", { chat_id });
};

export const markChatRead = (chat_id: string): void => {
  socket?.emit("mark-read", { chat_id });
};

export const onReceiveMessage = (
  callback: (message: SocketMessage) => void,
): (() => void) => {
  socket?.on("receiveMessage", callback);
  return () => socket?.off("receiveMessage", callback);
};

export const onReceiveChannelMessage = (
  callback: (message: SocketMessage) => void,
): (() => void) => {
  socket?.on("receive-channel-message", callback);
  return () => socket?.off("receive-channel-message", callback);
};

export const onUserTyping = (
  callback: (event: TypingEvent) => void,
): (() => void) => {
  socket?.on("user-typing", callback);
  return () => socket?.off("user-typing", callback);
};

export const onUserStopTyping = (
  callback: (event: TypingEvent) => void,
): (() => void) => {
  socket?.on("user-stop-typing", callback);
  return () => socket?.off("user-stop-typing", callback);
};

export const onPresenceUpdate = (
  callback: (event: PresenceEvent) => void,
): (() => void) => {
  socket?.on("presence-update", callback);
  return () => socket?.off("presence-update", callback);
};

/**
 * Bulk presence snapshot — server emits this in response to "get-online-users".
 * The payload is the list of currently-online user ids.
 */
export const onOnlineUsersSnapshot = (
  callback: (userIds: string[]) => void,
): (() => void) => {
  socket?.on("online-users", callback);
  return () => socket?.off("online-users", callback);
};

/**
 * Disparado cuando otro participante creó un chat en el que estamos. El
 * cliente debe upsertear el chat (si trae `chat`) o hacer un refresh de
 * la lista (si solo trae `chat_id`).
 */
export const onChatCreated = (
  callback: (event: ChatCreatedPayload) => void,
): (() => void) => {
  socket?.on("chat-created", callback);
  return () => socket?.off("chat-created", callback);
};

/**
 * Listener combinado de connect/disconnect para mantener un único flag
 * `isSocketConnected` sincronizado. Devuelve un unsubscribe.
 *
 * Nota: el `connect` de socket.io-client se dispara TANTO en la primera
 * conexión como en cada reconexión exitosa, así que no necesitamos
 * subscribirnos por separado a `manager.on("reconnect")`. Sí registramos
 * `connect_error` para bajar el flag cuando la handshake falla.
 */
export const onConnectionState = (
  onUp: () => void,
  onDown: () => void,
): (() => void) => {
  if (!socket) return () => {};
  const handleUp = () => onUp();
  const handleDown = () => onDown();
  socket.on("connect", handleUp);
  socket.on("disconnect", handleDown);
  socket.on("connect_error", handleDown);
  return () => {
    socket?.off("connect", handleUp);
    socket?.off("disconnect", handleDown);
    socket?.off("connect_error", handleDown);
  };
};

/**
 * Listener para reconexiones (manager-level). Útil para refrescar
 * `GET /chats` y recuperar conversaciones / mensajes que llegaron
 * mientras estabamos offline (CHAT_INTEGRATION.md §7.10).
 */
export const onReconnect = (callback: () => void): (() => void) => {
  if (!socket) return () => {};
  const handler = () => callback();
  socket.io.on("reconnect", handler);
  return () => {
    socket?.io?.off("reconnect", handler);
  };
};

export const requestOnlineUsersSnapshot = (): void => {
  socket?.emit("get-online-users");
};

export default {
  init: initChatSocket,
  isConfigured: isChatSocketConfigured,
  get: getChatSocket,
  disconnect: disconnectChatSocket,
  sendTyping,
  sendStopTyping,
  markChatRead,
  onReceiveMessage,
  onReceiveChannelMessage,
  onUserTyping,
  onUserStopTyping,
  onPresenceUpdate,
  onOnlineUsersSnapshot,
  onChatCreated,
  onConnectionState,
  onReconnect,
  requestOnlineUsersSnapshot,
};
